import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Wand2 } from "lucide-react";
import api from "../../lib/api";
import { Button } from "../ui/button";

const UPGRADES = [
  { id: "dialogue", label: "Add dialogue", on: true },
  { id: "journey", label: "Show the journey", on: true },
  { id: "gestures", label: "Gestures & expressions", on: true },
  { id: "context", label: "Story context & hook", on: false },
  { id: "camera", label: "Camera & pacing", on: false },
];

export const ImproveScenePanel = ({ videoId, prompt, title, duration, onImproved }) => {
  const [picked, setPicked] = useState(() => new Set(UPGRADES.filter((u) => u.on).map((u) => u.id)));
  const [loading, setLoading] = useState(false);
  const [fit, setFit] = useState(null);

  const toggle = (id) => setPicked((p) => {
    const n = new Set(p);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const improve = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/story/improve-suggestion", {
        video_id: videoId, prompt, title, upgrades: [...picked], duration,
      });
      onImproved(data.prompt);
      setFit(data);
      toast.success("Scene improved - review and edit it below.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't improve this scene");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="improve-scene-panel" className="space-y-3 rounded-lg border border-[#A855F7]/30 bg-[#A855F7]/5 p-3">
      <p className="text-sm font-medium text-white">Improve this scene</p>
      <div className="flex flex-wrap gap-2">
        {UPGRADES.map((u) => (
          <button key={u.id} type="button" onClick={() => toggle(u.id)} data-testid={`improve-option-${u.id}`}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${picked.has(u.id) ? "border-[#A855F7] bg-[#A855F7]/20 text-white" : "border-border/60 text-muted-foreground hover:text-white"}`}>
            {u.label}
          </button>
        ))}
      </div>
      <Button onClick={improve} disabled={loading || !picked.size || !prompt.trim()} size="sm" data-testid="improve-scene-btn"
        className="bg-[#A855F7] text-white hover:bg-[#9333EA]">
        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Wand2 className="mr-2 h-4 w-4" />}
        Improve scene
      </Button>
      {fit && (
        <p data-testid="improve-scene-fit" className={`text-xs ${fit.fits ? "text-[#00FF94]" : "text-amber-300"}`}>
          {fit.fits ? `Fits your ${fit.duration}s episode (about ${Math.round(fit.estimated_seconds)}s of lines and actions).` : fit.advice}
        </p>
      )}
    </div>
  );
};
